import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Modal } from '../components'
import { informeNaoMostrar, modalClose } from './redux/layoutActions'

export default ({}) => {
  const dispatch = useDispatch()
  const { sistema, statusModal } = useSelector(state => state.layoutState)
  const [informe, setInforme] = useState({ 
    title: '', 
    pular: '', 
    confirmarTitulo: '',
    confirmarMensagem: '',
    confirmar: '', 
    cancelar: '',
    closeText: '',
    videoUrl: ''
  })
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);

  const playerRef = useRef(null);
  const iframeContainerRef = useRef(null);

  // NEW: tempo salvo e flag para retomar tocando
  const [tempoSalvo, setTempoSalvo] = useState(0);
  const [shouldAutoplayOnReturn, setShouldAutoplayOnReturn] = useState(false);

  // Carrega as strings de tradução
  useEffect(() => {
    const nls = require(`./nls/${sistema.idioma}.json`);
    setInforme(nls.modalInforme);
  }, [sistema.idioma]);

  // Configura o player do YouTube quando o modal abre
  useEffect(() => {
    // Se o modal principal não está aberto, não carregar player
    if (statusModal !== 'modalInforme' || !informe.videoUrl || showSkipConfirm) return;

    const videoId = getVideoIdFromUrl(informe.videoUrl);
    if (!videoId) return;

    const loadYouTubeAPI = () => {
      if (window.YT && window.YT.Player) {
        initializePlayer(videoId);
        return;
      }
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      window.onYouTubeIframeAPIReady = () => initializePlayer(videoId);
    };

    const initializePlayer = (videoId) => {
      if (playerRef.current) return; // Já foi inicializado

      const iframe = document.createElement('iframe');
      iframe.width = "100%";
      iframe.height = "100%";
      // habilita JS API e melhora UX
      iframe.src = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1&playsinline=1`;
      iframe.frameBorder = "0";
      iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
      iframe.allowFullScreen = true;
      
      iframeContainerRef.current.innerHTML = '';
      iframeContainerRef.current.appendChild(iframe);

      playerRef.current = new window.YT.Player(iframe, {
        videoId,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
          playsinline: 1
        },
        events: {
          onReady: () => {
            // NEW: ao (re)abrir, se houver tempo salvo, faz seek e (opcional) play
            if (tempoSalvo && tempoSalvo > 0) {
              try {
                playerRef.current.seekTo(tempoSalvo, true);
              } catch {}
            }
            if (shouldAutoplayOnReturn) {
              try {
                playerRef.current.playVideo();
              } catch {}
              // reseta a flag para não ficar tocando sozinho em outras aberturas
              setShouldAutoplayOnReturn(false);
            }
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.ENDED) {
              handleVideoEnd();
            }
          }
        }
      });
    };

    loadYouTubeAPI();

    // Cleanup: destruir player quando o modal de vídeo sai de cena
    return () => {
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
    // IMPORTANTE: depende de showSkipConfirm, pois escondemos o modal principal quando o de confirmação abre
  }, [statusModal, informe.videoUrl, showSkipConfirm, tempoSalvo, shouldAutoplayOnReturn]);

  const getVideoIdFromUrl = (url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Quando o usuário clica em "Pular vídeo"
  const handleSkipVideo = () => {
    // NEW: capturamos o tempo atual ANTES de esconder o player
    try {
      const t = playerRef.current?.getCurrentTime ? playerRef.current.getCurrentTime() : 0;
      setTempoSalvo(t || 0);
      playerRef.current?.pauseVideo && playerRef.current.pauseVideo();
    } catch {}
    setShowSkipConfirm(true);
  };

  // Quando o vídeo termina
  const handleVideoEnd = async () => {
    setVideoEnded(true);
    try {
      await dispatch(informeNaoMostrar()).unwrap();
    } catch (error) {
      console.error('Erro ao marcar vídeo como visto:', error);
    } finally {
      dispatch(modalClose());
    }
  };

  // Usuário confirma que quer pular (não ver mais)
  const handleConfirmSkip = async () => {
    try {
      await dispatch(informeNaoMostrar()).unwrap();
    } catch (error) {
      console.error('Erro ao marcar vídeo como visto:', error);
    } finally {
      dispatch(modalClose());
      setShowSkipConfirm(false);
    }
  };

  // Usuário decide continuar assistindo
  const handleContinueWatching = () => {
    // NEW: sinaliza que, ao voltar, deve dar play (gesto do usuário)
    setShouldAutoplayOnReturn(true);
    setShowSkipConfirm(false);
    // o efeito de (re)montagem do player cuidará do seek e do play
  };

  const renderSkipConfirm = () => {
    return (
      <Modal
        title={informe?.confirmarTitulo || "Confirmar"}
        size='medium'
        open={true}
      >
        <div className='modal-skip-confirm'>
          <p>{informe?.confirmarMensagem || "Você tem certeza?"}</p>
          <div className='box-btns'>
            <Button
              type='btn block'
              color='primary'
              onClick={handleContinueWatching}
            >
              {informe?.cancelar || "Continuar assistindo"}
            </Button>
            <Button
              type='btn block'
              onClick={handleConfirmSkip}
            >
              {informe?.confirmar || "Sim, pular"}
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const renderVideoContent = () => {
    const videoId = getVideoIdFromUrl(informe.videoUrl);
    if (!videoId) return <p>URL do vídeo inválida.</p>;

    return (
      <>
        <div className='video-container'>
          <div 
            ref={iframeContainerRef}
            style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          />
        </div>
        <div className='box-skip-btn'>
          <Button
            type='link'
            color='primary'
            onClick={handleSkipVideo}
            disabled={videoEnded}
          >
            {informe?.pular || "Pular vídeo"}
          </Button>
        </div>
      </>
    );
  };

  return (
    <>
      {/* Modal Principal (Vídeo) - só renderiza se o de confirmação NÃO estiver aberto */}
      {!showSkipConfirm && (
        <Modal
          title={informe?.title || "Bem-vindo!"}
          size='large'
          open={statusModal === 'modalInforme'}
        >
          <div className='modal-modalInforme'>
            {renderVideoContent()}
          </div>
        </Modal>
      )}

      {/* Modal de Confirmação */}
      {showSkipConfirm && renderSkipConfirm()}
    </>
  );
};
